import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import api from '../../../utils/axios';
import { Card } from '../../../components/ui/card';
import { FechaPicker } from './FechaPicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { fmtNum } from './kpiUtils';
import { fmtFecha, fmtFechaHora } from './atencionUtils';

const TODOS = '__todos__';

// C5: registro de descargas del informe semanal (solo lectura).
export const AtencionRegistroInformes = () => {
  const [f, setF] = useState({ usuario: TODOS, desde: '', hasta: '' });
  const [data, setData] = useState({ registros: [], usuarios: [] });
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = { desde: f.desde || undefined, hasta: f.hasta || undefined, usuario: f.usuario === TODOS ? undefined : f.usuario };
      setData((await api.get('/kpis/atencion/informes-log', { params })).data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el registro');
    } finally {
      setCargando(false);
    }
  }, [f]);
  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Card className="p-5 lg:p-6" data-testid="atencion-registro">
      <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-800"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Registro de informes</h2>
      <p className="mb-4 text-xs text-slate-500">Cada descarga del informe semanal queda registrada y no se puede editar ni borrar.</p>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={f.usuario} onValueChange={(v) => setF((x) => ({ ...x, usuario: v }))}>
          <SelectTrigger className="h-9 w-64" data-testid="atencion-registro-usuario"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos los usuarios</SelectItem>
            {(data.usuarios || []).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
        <FechaPicker value={f.desde} max={f.hasta || undefined} placeholder="Desde" onChange={(v) => setF((x) => ({ ...x, desde: v }))} testId="atencion-registro-desde" />
        <span className="text-sm text-slate-400">a</span>
        <FechaPicker value={f.hasta} min={f.desde || undefined} placeholder="Hasta" onChange={(v) => setF((x) => ({ ...x, hasta: v }))} testId="atencion-registro-hasta" />
        {(f.desde || f.hasta) && (
          <button type="button" onClick={() => setF((x) => ({ ...x, desde: '', hasta: '' }))} className="text-xs text-sky-700 hover:underline" data-testid="atencion-registro-limpiar">Limpiar fechas</button>
        )}
        <span className="ml-auto text-xs text-slate-500" data-testid="atencion-registro-total">{fmtNum(data.total)} descargas</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr><th className="px-3 py-2 font-medium">Fecha y hora</th><th className="px-3 py-2 font-medium">Usuario</th>
              <th className="px-3 py-2 font-medium">Semana</th><th className="px-3 py-2 text-right font-medium">Tickets</th></tr>
          </thead>
          <tbody>
            {(data.registros || []).map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`atencion-registro-${r.id}`}>
                <td className="whitespace-nowrap px-3 py-2">{fmtFechaHora(r.fecha_hora)}</td>
                <td className="px-3 py-2">{r.usuario_nombre} <span className="text-xs text-slate-400">{r.usuario_email}</span></td>
                <td className="whitespace-nowrap px-3 py-2">{fmtFecha(r.semana_desde)} al {fmtFecha(r.semana_hasta)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.cantidad_tickets)}</td>
              </tr>
            ))}
            {!cargando && !(data.registros || []).length && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Sin descargas registradas.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
